import {useEffect, useState, useRef, useContext} from "react";
import Button from "react-bootstrap/Button";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";

import AuthContext from "context/AuthContext";
import {SwalAlertClass} from "core/swalClass";
import ModalWaypointSelects from "site-apps/distribution/waypoints/ModalWaypointSelects";
import WaypointsSelectorDropdown from "site-apps/distribution/waypoints/WaypointsSelectorDropdown";
import axiApi from "ApiCaller";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {subscribeEvent, unsubscribeEvent} from "core/eventsConnector";
import {TooltipTravelTime} from "./TooltipsIdentify";
import useCustomTranslation from "core/customTranslation";
import translations from "./translations";

const ParamsModel = {
    optimize: true,
    period: 0,
    origin: "",
    destination: "",
    unload: "",
    unload_type: "",
    unload_time: 0,
    refueling: "",
    refueling_time: 0,
    refueling_place: "s",
    duration: "",
    filling: 90,
    days: 5,
    force: false,
    c1: true,
    c2: true,
    c3: true,
    c4: true,
    c4_hours: 6,
    c5: true,
    c5_hours: 16,
    c6: true,
    c6_hours: 24,
};

// Criterios por defecto de los contenedores (checkbox): forzado y C1/C2/C3
const CRITERIA_CHECKS = [
    {name: "force", label: "Forzar recogida"},
    {name: "c1", label: "C1: nivel actual por encima del riesgo"},
    {name: "c2", label: "C2: previsión al final de la ruta"},
    {name: "c3", label: "C3: riesgo en la próxima ruta si no se recoge"},
];

// C4/C5/C6: tiempo sin comunicar; cada uno con sus horas (solo de plantilla, no se copian a los contenedores)
const NO_DATA_CHECKS = [
    {name: "c4", hours: "c4_hours", label: "C4: sin comunicar más de"},
    {name: "c5", hours: "c5_hours", label: "C5: sin comunicar más de"},
    {name: "c6", hours: "c6_hours", label: "C6: sin comunicar más de"},
];

// Criterios por defecto de la plantilla que se copian en cada contenedor (TabFilters.buildDynamicAssets)
const CONTAINER_CRITERIA = ["c1", "c2", "c3", "force", "days"];

const TabIdentify = ({formData, setFormData, dynamicAssets, stepsTabs, setKeyTab, pointsModels, editing}) => {
    const {panel} = useContext(AuthContext);
    const {t: _CT} = useCustomTranslation(translations);
    const Alert = new SwalAlertClass({confirmButtonText: "Aceptar"});
    const [waypoints, setWaypoints] = useState([]);
    const [params, setParams] = useState({...ParamsModel});
    const [showModalWaypoints, setShowModalWaypoints] = useState(false);
    const waypointTo = useRef("");
    const listPointsModels = ["origin", "destination", "unload", "refueling"];
    const toInteger = ["period", "unload_time", "refueling_time", "days", "filling", "c4_hours", "c5_hours", "c6_hours"];

    useEffect(() => {
        if (editing.data.isNew) {
            axiApi.get("/api/distribution/waypoints/").then((resp) => {
                setWaypoints([...resp.data.items]);
                const params_ = {...params};
                params_.period = panel.unloading;
                setParams(params_);
            });
        }
        return () => {
            unsubscribeEvent("waypoints:modal:selector", eventModalWaypoints);
        };
    }, []);

    useEffect(() => {
        if (editing.data.identify) {
            // console.log("[TabIdentify].useEffect()[editing.data.identify]");
            axiApi.get("/api/distribution/waypoints/").then((resp) => {
                setWaypoints([...resp.data.items]);
                const params_ = {...formData.params};
                resp.data.items.forEach((item) => {
                    listPointsModels.forEach((name) => {
                        if (params_[name] && item.id === params_[name]) {
                            switch (name) {
                                case "refueling":
                                    item.place = params_["refueling_place"];
                                    item.time = params_["refueling_time"];
                                    break;
                                case "unload":
                                    item.time = params_["unload_time"];
                                    break;
                                default:
                                    break;
                            }
                            pointsModels.current[name] = item;
                            params_[name] = item.id;
                        }
                    });
                });
                setParams(params_);
                setFormData({...formData, params: {...params_}});
                editing.set("filters", true);
            });
        }
        return () => {
            unsubscribeEvent("waypoints:modal:selector", eventModalWaypoints);
        };
    }, [editing.data.identify]);

    const normalizeParamValue = (name, value) => {
        if (toInteger.includes(name)) {
            if (value === "" || value === null || value === undefined) return "";
            return parseInt(value, 10);
        }
        return value;
    };

    const updateParams = (name, value, extra = {}) => {
        const normalized = normalizeParamValue(name, value);

        setParams((prev) => ({
            ...prev,
            [name]: normalized,
            ...extra,
        }));
        // console.log(`Updated param '${name}' to '${normalized}'`);

        if (stepsTabs.getCurrentSteps().filters) {
            stepsTabs.disableTab("filters");
            stepsTabs.disableTab("criteria");
            stepsTabs.disableTab("preview");
            stepsTabs.setAddAssets(false);
            stepsTabs.setFiltersDynamic(false);
            stepsTabs.setClassifyAssets(false);
        }
        if (name === "refueling_place") {
            pointsModels.current.unload.place = normalized;
        }

        if (listPointsModels.includes(name)) {
            const item = waypoints.find((item) => item.id === normalized);
            switch (name) {
                case "refueling":
                    item.place = params["refueling_place"];
                    item.time = params["refueling_time"];
                    break;
                case "unload":
                    item.time = params["unload_time"];
                    break;
                default:
                    break;
            }
            pointsModels.current[name] = item;
        }
    };

    const handleChangeData = (e) => {
        const {name, value} = e.target;
        setFormData({...formData, [name]: value});
        if (stepsTabs.getCurrentSteps().filters) {
            stepsTabs.disableTab("filters");
            stepsTabs.disableTab("criteria");
            stepsTabs.disableTab("preview");
            stepsTabs.setAddAssets(false);
            stepsTabs.setFiltersDynamic(false);
            stepsTabs.setClassifyAssets(false);
        }
    };

    const handleChangeParams = (e) => {
        const {name, type, value, checked} = e.target;
        updateParams(name, type === "checkbox" ? checked : value);
    };

    const handleChangeWaypoint = (name, _item) => {
        if (_item === null) {
            updateParams(name, null);
            return;
        }

        let extra = {};
        switch (name) {
            case "unload":
                extra.unload_time =
                    params.unload_time === 0 || params.unload_time === "0" || params.unload_time === ""
                        ? _item.time
                        : params.unload_time;
                break;
            case "refueling":
                extra.refueling_time =
                    params.refueling_time === 0 || params.refueling_time === "0" || params.refueling_time === ""
                        ? _item.time
                        : params.refueling_time;
                break;
        }
        updateParams(name, _item.id, extra);
    };
    const handleShowModalWaypoints = (e) => {
        waypointTo.current = e.currentTarget.value;
        setShowModalWaypoints(true);
        subscribeEvent("waypoints:modal:selector", eventModalWaypoints);
    };
    const eventModalWaypoints = (e) => {
        updateParams(waypointTo.current, e.detail.id);
        unsubscribeEvent("waypoints:modal:selector", eventModalWaypoints);
    };

    // Los contenedores con el valor anterior de la plantilla toman el nuevo; los personalizados se quedan igual.
    const propagateCriteria = (previous, next) => {
        CONTAINER_CRITERIA.filter((name) => previous[name] !== next[name]).forEach((name) => {
            dynamicAssets.current.forEach((dyn) => {
                if (dyn[name] === previous[name]) dyn[name] = next[name];
            });
        });
    };

    const onClickNext = () => {
        const noDataWithoutHours = NO_DATA_CHECKS.find((check) => params[check.name] && !params[check.hours]);
        if (formData.name === "") {
            Alert.warning("Debe ingresar un nombre");
        } else if (params.period === "0" || params.period === 0 || params.period === "") {
            Alert.warning("No se ha introducido la Duración de cada recogida (min)");
        } else if (params.origin === "") {
            Alert.warning("Se tiene que seleccionar un Punto de origen");
        } else if (params.destination === "") {
            Alert.warning("Se tiene que seleccionar un Punto de destino");
        } else if (params.duration === "") {
            Alert.warning("Se tiene que seleccionar una Duración de la ruta");
        } else if (noDataWithoutHours) {
            Alert.warning(`Indique las horas de ${noDataWithoutHours.name.toUpperCase()}`);
        } else {
            const params_ = {...params};
            params_.period = parseInt(params_.period);
            propagateCriteria(formData.params, params_);
            setFormData({...formData, params: {...params_}});
            stepsTabs.enableTab("filters");
            setKeyTab("filters");
        }
    };

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-md-6 col-12 mb-4">
                    <div className="row">
                        <div className="col-md-8 col-12">
                            <label htmlFor="name" className="form-label">
                                Nombre*
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleChangeData}
                                placeholder="Nombre identificativo"
                            />
                        </div>
                        <div className="col-md-4 col-12 pt-4 mt-3">
                            <div className="form-check">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="optimize"
                                    name="optimize"
                                    checked={params.optimize}
                                    onChange={handleChangeParams}
                                />
                                <label htmlFor="optimize" className="form-check-label">
                                    {_CT("optimize-route")}
                                </label>
                            </div>
                        </div>
                    </div>
                    <div className="col-12 mt-3">
                        <label htmlFor="description" className="form-label">
                            Descripción
                        </label>
                        <textarea
                            rows="4"
                            className="form-control"
                            id="description"
                            name="description"
                            value={formData.description}
                            onChange={handleChangeData}
                        />
                    </div>
                </div>
                <div className="col-md-6 col-12 mb-4">
                    <div className="row">
                        <div className="col-12 text-end">
                            <button type="button" className="btn btn-primary" onClick={onClickNext}>
                                Siguiente
                            </button>
                        </div>
                    </div>
                    <div className="row d-flex align-items-center mt-4 text-center">
                        <div className="text-center">Criterios por defecto de los contenedores</div>
                    </div>
                    <div className="row d-flex align-items-center mt-3">
                        <div className="col text-end" style={{maxWidth: "50%"}}>
                            Días desde el último vaciado
                        </div>
                        <div className="col">
                            <input
                                type="number"
                                className="form-control text-end"
                                name="days"
                                min="0"
                                value={params.days}
                                onChange={handleChangeParams}
                                style={{maxWidth: "90px"}}
                                title="0 = no se aplica"
                            />
                        </div>
                    </div>
                    <div className="row d-flex align-items-center mt-2">
                        <div className="col-md-6 col-12">
                            {CRITERIA_CHECKS.map((check) => (
                                <div key={check.name} className="row d-flex align-items-center mt-2">
                                    <div className="col d-flex justify-content-center">
                                        <div className="form-check" style={{minWidth: "320px"}}>
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id={`params-${check.name}`}
                                                name={check.name}
                                                onChange={handleChangeParams}
                                                checked={params[check.name]}
                                            />
                                            <label className="form-check-label" htmlFor={`params-${check.name}`}>
                                                {check.label}
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="col-md-6 col-12">
                            {NO_DATA_CHECKS.map((check) => (
                                <div key={check.name} className="row d-flex align-items-center mt-2">
                                    <div className="col d-flex justify-content-center">
                                        <div className="form-check d-flex align-items-center" style={{minWidth: "320px"}}>
                                            <input
                                                className="form-check-input me-2"
                                                type="checkbox"
                                                id={`params-${check.name}`}
                                                name={check.name}
                                                onChange={handleChangeParams}
                                                checked={params[check.name]}
                                            />
                                            <label className="form-check-label me-2" htmlFor={`params-${check.name}`}>
                                                {check.label}
                                            </label>
                                            <input
                                                type="number"
                                                className="form-control form-control-sm text-end"
                                                name={check.hours}
                                                min="1"
                                                value={params[check.hours]}
                                                onChange={handleChangeParams}
                                                disabled={!params[check.name]}
                                                style={{maxWidth: "70px"}}
                                            />
                                            <span className="ms-1">h</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>


                </div>
            </div>
            <div className="row mt-3">
                <div className="col-12 mb-4">
                    <h3>Parámetros</h3>
                </div>
            </div>
            <div className="row mb-5">
                <div className="col-md-4 col-12">
                    <h6 className="text-center">* Campos obligatorios</h6>
                    <div className="row d-flex align-items-center">
                        <div className="col text-end">Duración de cada recogida (min)*</div>
                        <div className="col">
                            <input
                                type="number"
                                className="form-control text-end d-inline-block"
                                style={{width: "80%"}}
                                name="period"
                                value={params.period}
                                onChange={handleChangeParams}
                            />
                            <OverlayTrigger placement="top" overlay={TooltipTravelTime}>
                                <span>
                                    <FontAwesomeIcon icon="circle-info" className="ms-2 text-info" size="lg"/>
                                </span>
                            </OverlayTrigger>
                        </div>
                    </div>
                    <div className="row d-flex align-items-center mt-4">
                        <div className="col text-end">Punto de origen*</div>
                        <div className="col">
                            <ButtonGroup>
                                <WaypointsSelectorDropdown
                                    waypoints={waypoints}
                                    name="origin"
                                    onChange={handleChangeWaypoint}
                                    selectedId={params.origin}
                                />
                                <Button
                                    variant="outline-secondary"
                                    type="button"
                                    onClick={handleShowModalWaypoints}
                                    value="origin">
                                    <FontAwesomeIcon icon="fa-solid fa-magnifying-glass"/>
                                </Button>
                            </ButtonGroup>
                        </div>
                    </div>
                    <div className="row d-flex align-items-center mt-4">
                        <div className="col text-end">Punto de destino*</div>
                        <div className="col">
                            <ButtonGroup>
                                <WaypointsSelectorDropdown
                                    waypoints={waypoints}
                                    name="destination"
                                    onChange={handleChangeWaypoint}
                                    selectedId={params.destination}
                                />
                                <Button
                                    variant="outline-secondary"
                                    type="button"
                                    onClick={handleShowModalWaypoints}
                                    value="destination">
                                    <FontAwesomeIcon icon="fa-solid fa-magnifying-glass"/>
                                </Button>
                            </ButtonGroup>
                        </div>
                    </div>
                    <div className="row d-flex align-items-center mt-4">
                        <div className="col text-end">Duración de la ruta*</div>
                        <div className="col">
                            <input
                                type="time"
                                className="form-control"
                                style={{maxWidth: "90px"}}
                                name="duration"
                                value={params.duration}
                                onChange={handleChangeParams}
                            />
                        </div>
                    </div>
                </div>
                <div className="col-md-4 col-12">
                    <h6 className="text-center">Descarga</h6>
                    <div className="row d-flex align-items-center mt-4">
                        <div className="col text-end">Punto de descarga</div>
                        <div className="col">
                            <ButtonGroup>
                                <WaypointsSelectorDropdown
                                    waypoints={waypoints}
                                    name="unload"
                                    onChange={handleChangeWaypoint}
                                    selectedId={params.unload}
                                    empty={true}
                                />
                                <Button
                                    variant="outline-secondary"
                                    type="button"
                                    onClick={handleShowModalWaypoints}
                                    value="unload">
                                    <FontAwesomeIcon icon="fa-solid fa-magnifying-glass"/>
                                </Button>
                            </ButtonGroup>
                        </div>
                    </div>
                    <div className="row d-flex align-items-center mt-4">
                        <div className="col text-end">Duración de descarga</div>
                        <div className="col">
                            <input
                                type="number"
                                name="unload_time"
                                className="form-control text-end"
                                value={params.unload_time}
                                onChange={handleChangeParams}
                            />
                        </div>
                    </div>
                </div>
                <div className="col-md-4 col-12">
                    <h6 className="text-center">Repostaje</h6>
                    <div className="row d-flex align-items-center mt-4">
                        <div className="col text-end">Punto de repostaje</div>
                        <div className="col">
                            <ButtonGroup>
                                <WaypointsSelectorDropdown
                                    waypoints={waypoints}
                                    name="refueling"
                                    onChange={handleChangeWaypoint}
                                    selectedId={params.refueling}
                                    empty={true}
                                />
                                <Button
                                    variant="outline-secondary"
                                    type="button"
                                    onClick={handleShowModalWaypoints}
                                    value="refueling">
                                    <FontAwesomeIcon icon="fa-solid fa-magnifying-glass"/>
                                </Button>
                            </ButtonGroup>
                        </div>
                    </div>
                    <div className="row d-flex align-items-center mt-4">
                        <div className="col text-end">Duración de repostaje</div>
                        <div className="col">
                            <input
                                type="number"
                                name="refueling_time"
                                className="form-control text-end"
                                value={params.refueling_time}
                                onChange={handleChangeParams}
                            />
                        </div>
                    </div>
                    <div className="row d-flex align-items-center mt-4">
                        <div className="col text-end">Posición del respostaje</div>
                        <div className="col">
                            <select
                                name="refueling_place"
                                className="form-select"
                                value={params.refueling_place}
                                onChange={handleChangeParams}>
                                <option value="s">Inicio</option>
                                <option value="e">Fin</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>
            {showModalWaypoints ? (
                <ModalWaypointSelects
                    show={showModalWaypoints}
                    setShow={setShowModalWaypoints}
                    multiple={false}
                    subtitle={document.title}
                />
            ) : (
                <></>
            )}
        </div>
    );
};

export default TabIdentify;
